import { useEffect } from "react";
import { ArrowUpRight, ContactRound, Globe } from "lucide-react";
import { ReactComponent as WhatsAppLogo } from "@/assets/icons/whatsapp.svg";
import contact from "@/lib/contact.json";

const connectUrl = `${contact.website}/connect`;
const whatsappUrl = `https://wa.me/${contact.phone}?text=${encodeURIComponent(contact.connectMessage)}`;

function useConnectMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = contact.metaTitle;
    const changes = [];
    const setHeadElement = (selector, tag, attributes) => {
      let element = document.head.querySelector(selector);
      const previous = element?.getAttribute(tag === "meta" ? "content" : "href");
      const created = !element;
      if (created) {
        element = document.createElement(tag);
        document.head.appendChild(element);
      }
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      changes.push(() => {
        if (created) element.remove();
        else if (previous !== null) element.setAttribute(tag === "meta" ? "content" : "href", previous);
        else element.removeAttribute(tag === "meta" ? "content" : "href");
      });
    };
    setHeadElement('meta[name="description"]', "meta", { name: "description", content: contact.metaDescription });
    for (const [property, content] of Object.entries({
      "og:title": contact.metaTitle,
      "og:description": contact.metaDescription,
      "og:type": "website",
      "og:url": connectUrl,
      "og:site_name": contact.organization,
      "og:image": `${contact.website}/panchmaya-mark-social.png`,
      "og:image:alt": "The five-petal Panchmaya logo",
    })) setHeadElement(`meta[property="${property}"]`, "meta", { property, content });
    setHeadElement('meta[name="twitter:card"]', "meta", { name: "twitter:card", content: "summary" });
    setHeadElement('link[rel="canonical"]', "link", { rel: "canonical", href: connectUrl });
    return () => {
      document.title = previousTitle;
      changes.reverse().forEach((restore) => restore());
    };
  }, []);
}

export default function Connect() {
  useConnectMetadata();

  return (
    <main className="connect-page" data-testid="connect-page">
      <article className="connect-card" aria-labelledby="connect-name">
        <header className="connect-brand">
          <a href={contact.website} aria-label="Visit Panchmaya's website" className="connect-logo-link">
            <img src="/panchmaya-mark-320.webp" width="112" height="112" alt="Panchmaya five-petal logo" fetchPriority="high" className="connect-logo" />
          </a>
          <p className="font-display connect-wordmark"><span>Panch</span><span>maya</span></p>
          <p className="connect-tagline">From darkness to light</p>
        </header>

        <div className="connect-divider" aria-hidden="true"><span /></div>

        <section className="connect-profile" aria-labelledby="connect-name">
          <img src="/sheetal-garg-320.webp" width="76" height="76" alt="Sheetal Garg" className="connect-portrait" />
          <h1 id="connect-name" className="font-display connect-name">{contact.name}</h1>
          <p className="connect-role">{contact.title}</p>
          <p className="connect-company">{contact.organization}</p>
          <p className="connect-intro">Helping you reconnect with yourself through mindful lifestyle coaching, personal growth, and intentional living.</p>
        </section>

        <nav className="connect-actions" aria-label="Connect with Sheetal">
          <a href={`/${contact.vCardFilename}`} className="connect-action connect-action-primary" type="text/vcard">
            <ContactRound aria-hidden="true" size={20} strokeWidth={1.5} />
            <span>Save My Contact</span>
            <span aria-hidden="true" className="connect-action-end">+</span>
          </a>
          <a href={whatsappUrl} className="connect-action connect-action-whatsapp" target="_blank" rel="noopener noreferrer">
            <WhatsAppLogo aria-hidden="true" focusable="false" className="h-5 w-5" />
            <span>Connect on WhatsApp</span>
            <ArrowUpRight aria-hidden="true" size={17} className="connect-action-end" />
          </a>
          <a href={contact.website} className="connect-action connect-action-secondary">
            <Globe aria-hidden="true" size={20} strokeWidth={1.5} />
            <span>Explore Panchmaya</span>
            <ArrowUpRight aria-hidden="true" size={17} className="connect-action-end" />
          </a>
        </nav>

        <footer className="connect-footer">
          <p className="font-display connect-philosophy">Perceive. Release. Reconnect.</p>
          <a href={contact.website}>Panchmaya · From darkness to light</a>
          <p className="connect-scope">Lifestyle coaching for personal growth and intentional living.</p>
        </footer>
      </article>
    </main>
  );
}
